import type { QueueStatsResponse } from "@bw/shared/types/generated";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

/**
 * Facility busyness presenter (from GET /queue/stats).
 *
 * Shows the live queue-group count and how many courts are open — the honest
 * signals for drop-in, which is QUEUE-based. One queue unit can be a solo
 * player or a party, so the count is always labelled "groups", never implied
 * to be people. It NEVER renders "spots left" (that's a capacity framing that
 * doesn't apply). An empty queue reports only that no groups are waiting; it
 * does not imply that a court is available.
 */
export function QueueBusynessCard({ stats }: { stats: QueueStatsResponse }) {
  const empty = stats.queue_size === 0;

  return (
    <Paper
      variant="outlined"
      role="group"
      aria-label="How busy it is"
      sx={{ p: 2 }}
    >
      <Typography variant="overline" color="primary.main">
        Right now
      </Typography>
      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
        {/* Each line is a SINGLE element (no nested element children) so the
            number and its caption read as one phrase: "5 groups waiting". */}
        <Typography sx={{ fontWeight: 800 }}>
          {empty
            ? "No groups waiting."
            : `${stats.queue_size} ${stats.queue_size === 1 ? "group" : "groups"} waiting`}
        </Typography>
        <Typography sx={{ fontWeight: 800 }}>
          {`${stats.courts_available}/${stats.courts_total} courts open`}
        </Typography>
      </Stack>
    </Paper>
  );
}
